/**
 * WP-23: who is signed in, from the page's side, and which repo live mode shows.
 *
 * The page holds no credential. Sign-in belongs to the accounts Worker on the same host (`/sign-in`), which sets
 * the session cookie; the page asks the dashboard Worker `GET /api/me` and gets the account back, or a 401 that
 * means "link to /sign-in". Approvals record `approvedBy` as the account's principal, `human:<accountId>`.
 *
 * Live mode shows one repo, named in the page's URL: `?org=<org>&repo=<repo>` (with `mode=live`). Every coordinator
 * call goes to this origin, `/api/orgs/<org>/repos/<repo>/coordinator/...`, which the dashboard Worker proxies.
 */

/** What `GET /api/me` answers for a signed-in account. */
export interface Me {
  accountId: string;
  /** `human:<accountId>`: what approvals record. */
  principal: string;
  name: string | null;
  email: string | null;
  orgs: { org: string; role: string }[];
}

export type AccountState = { status: "signed-in"; me: Me } | { status: "signed-out" } | { status: "unavailable"; message: string };

/** Where the accounts Worker signs people in, on this host. */
export const SIGN_IN_PATH = "/sign-in";
/** Account settings (PATs, CLI tokens, sign out), on this host. */
export const SETTINGS_PATH = "/settings";

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/** Ask the dashboard Worker who is signed in. Never throws. */
export async function loadAccount(doFetch: Fetch = (i, init) => fetch(i, init)): Promise<AccountState> {
  try {
    const res = await doFetch("/api/me", { credentials: "same-origin", cache: "no-store" });
    if (res.status === 401) return { status: "signed-out" };
    const body = (await res.json()) as { ok?: boolean; result?: Me; error?: { message?: string } };
    if (!res.ok || !body.ok || !body.result) return { status: "unavailable", message: body.error?.message ?? `HTTP ${res.status}` };
    return { status: "signed-in", me: body.result };
  } catch {
    return { status: "unavailable", message: "could not reach the dashboard" };
  }
}

const ORG = /^[a-z0-9](?:-?[a-z0-9])*$/;
const REPO = /^[A-Za-z0-9](?:[A-Za-z0-9._]|-(?!-))*$/;

/** The repo named in a page URL's `?org=&repo=`, or null when either is missing or not a valid name. */
export function repoFromUrl(href: string): { org: string; repo: string } | null {
  const q = new URL(href).searchParams;
  const org = q.get("org") ?? "";
  const repo = q.get("repo") ?? "";
  return ORG.test(org) && org.length <= 39 && REPO.test(repo) && repo.length <= 50 ? { org, repo } : null;
}

/** The dashboard proxy's coordinator base for a repo, absolute on this origin (WebSockets need an absolute URL). */
export function coordinatorBase(origin: string, ref: { org: string; repo: string }): string {
  return `${origin}/api/orgs/${encodeURIComponent(ref.org)}/repos/${encodeURIComponent(ref.repo)}/coordinator`;
}

/** A short name to show for the account. */
export function displayName(me: Me): string {
  return me.name || me.email || me.accountId;
}
